import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Auth,
  AuthCredential,
  ConfirmationResult,
  getAuth,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { Box, Paragraph, ParagraphExtendedProps, Text } from 'grommet';
import { withRouter } from 'react-router-dom';
import OTPVerification from './OTPVerification';
import MultipleUsersForPhoneNumber from './MultipleUsersForPhoneNumber';
import SHDBackButton from '@ui/BackButton/SHDBackButton';
import SHDContainer from '@ui/Container/SHDContainer';
import AlternativeLogin from './AlternativeLogin';
import FromSampleClipModal from './FromSampleClipModal';

// Helpers, hooks, services, and utilities
import { BasicLoader } from '@helpers/Loaders';
import { INVITED_USER } from '@constants/routes';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { theFirebase } from '@services/Firebase/firebase';
import { AuthenticateFunc, useAuthentication } from './authHooks';
import { useQuery } from '@helpers/utils';
import { useTeamPlayer } from '../Player/playerHooks';
import { useInvite } from '@hooks/useInvite';
import axios from '@helpers/axios';

// Types and enums
import { SignInState } from './SignIn.d';
import type { SignInProps, ErrorMessage } from './SignIn.d';
import { shd } from '@shd/jslib/models';
import AccountInfo from './AccountInfo';
import { AppHostContext } from '@services/appHost';
import * as MOBILE_SCREENS from '../../constants/mobileScreens';

const ErrorDiv = (props: ParagraphExtendedProps) => (
  <Paragraph color="status-error" margin="none" {...props} />
);

const SignIn: React.FC<SignInProps> = ({ history, location }) => {
  const query = useQuery();

  // State and refs
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);
  const [currentState, setCurrentState] = useState<SignInState>(
    SignInState.PhoneEntry
  );
  const [error, setError] = useState<ErrorMessage | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [existingUser, setExistingUser] = useState<shd.User | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [possibleUsers, setPossibleUsers] = useState<shd.User[]>([]);
  const [sendSMSLoading, setSendSMSLoading] = useState<boolean>(false);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const [headline, setHeadline] = useState<string>(
    'Sign in or register with your phone number'
  );
  const [showBackButton, setShowBackButton] = useState<boolean>(true);
  const [subHeadline, setSubHeadline] = useState<React.ReactElement>();
  const [showFromSampleClipModal, setShowFromSampleClipModal] =
    useState<boolean>(false);
  const appHost = useContext(AppHostContext);
  const { phoneProvider } = theFirebase().getAllAuthProviders();

  const key = query.get('key') || undefined;
  const inviteCode = query.get('code') || undefined;
  const teamPlayerIdToBeClaimed = query.get('claim_player') || undefined;

  const { authenticate, authStatus } = useAuthentication({
    errorHandler: setError,
    claimPlayer: teamPlayerIdToBeClaimed,
    claimPlayerKey: key,
    linkAccountHandler: () => {},
    postUrl: '/api/user',
    provider: phoneProvider,
    toggleLoading: setLoading,
  });

  const isProduction =
    import.meta.env.VITE_APP_FIREBASE_CONFIG === 'production';
  const isInvitedUser = !!inviteCode && location.pathname === INVITED_USER;
  const { teamPlayer } = useTeamPlayer(teamPlayerIdToBeClaimed || '');
  const {
    error: inviteError,
    expiration,
    inviteType,
    invitingUser,
    loading: inviteLoading,
    nameToManage,
    validCode,
  } = useInvite(inviteCode || '');

  // Handlers
  const handlePhoneSignIn = () => {
    if (!isValidPhoneNumber(phoneNumber) && isProduction) {
      setError({ message: 'Phone number region is unsupported.' });
      return;
    }

    setError(null);
    setSendSMSLoading(true);
    const auth = getAuth();
    renderRecaptcha(auth);
    window.recaptchaVerifier.verify();
  };

  const handleVerifyCode = async (verificationCode: string) => {
    setError(null);
    if (!confirmationResult) {
      return {
        success: false,
        message: 'No verification process initialized.',
      };
    }
    const credential = PhoneAuthProvider.credential(
      confirmationResult.verificationId,
      verificationCode
    );
    checkPhoneNumber(credential, verificationCode);
    return { success: true };
  };

  const resetState = () => {
    clearRecaptcha();
    setPhoneNumber('');
    setConfirmationResult(null);
    setError(null);
    setCurrentState(SignInState.PhoneEntry);
    const auth = getAuth();
    renderRecaptcha(auth);
  };

  const checkPhoneNumber = async (
    credential: AuthCredential,
    verificationCode: string
  ) => {
    try {
      const response = await axios.get('/api/signin/check_phone_number', {
        params: { phoneNumber, recaptchaToken: '' },
      });

      const users = response.data.users;
      if (users.length === 0) {
        const postParams = {
          phone: phoneNumber,
          sign_up: 1,
          invite_code: isInvitedUser ? inviteCode : undefined,
        };
        authenticate(credential, undefined, postParams);
      } else if (users.length === 1) {
        setExistingUser(users[0]);
        const postParams = {
          phone: phoneNumber,
          existing_user_id: users[0]._id,
          invite_code: isInvitedUser ? inviteCode : undefined,
        };
        authenticate(credential, existingUser, postParams);
      } else {
        confirmationResult
          ?.confirm(verificationCode)
          .then(async () => {
            await theFirebase().doSignOut(true);
            setCurrentState(SignInState.MultipleUsers);
            setPossibleUsers(users);
          })
          .catch(() => {
            setError({
              message: 'The code you entered is invalid. Please try again',
            });
          });
      }
    } catch (e) {
      setError({ message: 'Error fetching accounts. Please try again.' });
    }
  };

  // ReCAPTCHA-related methods
  const renderRecaptcha = (auth: Auth) => {
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      'recaptcha-container',
      {
        size: 'invisible',
        callback: () => {
          const appVerifier = window.recaptchaVerifier;
          signInWithPhoneNumber(auth, phoneNumber, appVerifier)
            .then((cr) => {
              setSendSMSLoading(false);
              window.confirmationResult = cr;
              setConfirmationResult(cr);
              setCurrentState(SignInState.OtpVerification);
            })
            .catch((e) => {
              setSendSMSLoading(false);
              setError({ message: e.message });
              clearRecaptcha();
            });
        },
        'expired-callback': () => {
          setSendSMSLoading(false);
        },
      }
    );
  };

  const clearRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }

    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML =
        '<div id="recaptcha-container"></div>';
    }
  };

  const handleAuthentication: AuthenticateFunc = useCallback(
    (credential, existingAuthUser, extraPostParams, providerOverrider) => {
      authenticate(
        credential,
        existingAuthUser,
        extraPostParams,
        providerOverrider
      );
    },
    [authenticate]
  );

  // Effects

  useEffect(() => {
    if (authStatus?.hasBeenAuthenticated && !authStatus?.collectUserInfo) {
      setLoading(false);
      history.push(authStatus.redirectUrl || '/');
    }
    if (authStatus?.collectUserInfo) {
      setLoading(false);
      setCurrentState(SignInState.CollectUserInfo);
    }
  }, [authStatus, history]);

  useEffect(() => {
    const { clip: c } = location.state || {};
    if (c) {
      setShowFromSampleClipModal(true);
    }
  }, [location]);

  useEffect(() => {
    if (teamPlayerIdToBeClaimed && teamPlayer) {
      setShowBackButton(false);
      setHeadline('Claim your player');
      setSubHeadline(
        <Text size="subhead">
          Sign in or register with your phone number to claim&nbsp;
          <Text weight={600}>
            {teamPlayer?.nameFirst} {teamPlayer?.nameLast}
          </Text>
        </Text>
      );
    }
  }, [teamPlayerIdToBeClaimed, teamPlayer]);

  useEffect(() => {
    if (inviteCode) {
      setShowBackButton(false);
    }

    if (inviteCode && inviteLoading) {
      setHeadline('Checking invitation...');
      return;
    }

    if (inviteError) {
      setHeadline('This invitation has expired or is invalid.');
      setError({ message: inviteError });
      return;
    }

    if (!validCode) {
      setHeadline('This invitation has been claimed or is expired.');
      return;
    }

    if (inviteCode && validCode) {
      const roleVerb = inviteType === 'coach' ? 'as a coach of' : 'to manage';
      setHeadline(`${invitingUser} invited you ${roleVerb} ${nameToManage}`);
      setSubHeadline(
        <Text size="subhead">
          Invite expires in {expiration?.days} days and {expiration?.hours}{' '}
          hours.
        </Text>
      );
    }
  }, [
    inviteError,
    inviteLoading,
    expiration,
    inviteCode,
    inviteType,
    invitingUser,
    nameToManage,
    validCode,
  ]);

  useEffect(() => {
    switch (currentState) {
      case SignInState.PhoneEntry:
      case SignInState.AlternativeLogin:
        setShowBackButton(false);
        break;
      case SignInState.MultipleUsers:
      case SignInState.OtpVerification:
      case SignInState.CollectUserInfo:
        setShowBackButton(true);
        break;
    }

    if (appHost.isHosted) {
      setShowBackButton(true);
    }
  }, [currentState, appHost.isHosted]);

  const handleGoBack = () => {
    switch (currentState) {
      case SignInState.PhoneEntry:
      case SignInState.AlternativeLogin:
        if (appHost.isHosted) {
          appHost.service.notifications.navigateTo(
            MOBILE_SCREENS.WELCOME_SCREEN
          );
        } else {
          history.goBack(); // Fallback if not hosted
        }
        break;

      case SignInState.MultipleUsers:
        setCurrentState(SignInState.PhoneEntry);
        break;

      case SignInState.OtpVerification:
        resetState();
        break;

      default:
        history.goBack(); // Default fallback
    }
  };

  // Refactored switch statement to determine which component to render
  const renderStep = () => {
    switch (currentState) {
      case SignInState.OtpVerification:
        return (
          <OTPVerification
            phoneNumber={phoneNumber}
            onVerify={handleVerifyCode}
            onResend={() => {
              setError(null);
              clearRecaptcha();
              handlePhoneSignIn();
            }}
          />
        );
      case SignInState.MultipleUsers:
        return (
          <MultipleUsersForPhoneNumber
            users={possibleUsers}
            onContinue={(selectedProvider) => {
              const postParams = {
                invite_code: isInvitedUser ? inviteCode : undefined,
              };
              authenticate(undefined, undefined, postParams, selectedProvider);
            }}
          />
        );

      case SignInState.CollectUserInfo:
        return (
          <AccountInfo
            inviteCode={inviteCode}
            redirectUrl={authStatus?.redirectUrl}
            fromInvite={(!!key && !!teamPlayerIdToBeClaimed) || !!inviteCode}
          />
        );

      case SignInState.PhoneEntry:
      case SignInState.AlternativeLogin:
      default:
        return (
          <AlternativeLogin
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            handlePhoneSignIn={handlePhoneSignIn}
            handleAuthentication={handleAuthentication}
            inviteCode={inviteCode}
            isInvitedUser={isInvitedUser}
            error={error}
            setError={setError}
            setLoading={setLoading}
            sendSMSLoading={sendSMSLoading}
            headline={headline}
            subHeadline={subHeadline}
          />
        );
    }
  };
  return (
    <>
      <SHDContainer maxWidth="small">
        <Box alignSelf="start" margin={{ bottom: 'xxlarge' }}>
          {showBackButton ? (
            <SHDBackButton onClick={handleGoBack} />
          ) : (
            <Box fill={false} width="2.25rem" height="2.25rem"></Box>
          )}
        </Box>

        {loading && <BasicLoader fullPage />}
        <div ref={recaptchaContainerRef}>
          <div id="recaptcha-container"></div>
        </div>
        {renderStep()}
        {!!error && <ErrorDiv>{error.message}</ErrorDiv>}
      </SHDContainer>
      {showFromSampleClipModal && (
        <FromSampleClipModal
          clip={location.state?.clip}
          onClose={() => {
            history.replace({
              pathname: location.pathname,
              search: location.search,
            });
            setShowFromSampleClipModal(false);
          }}
        />
      )}
    </>
  );
};

export default withRouter(SignIn);
